import React, { useState, useEffect } from "react";
import { Coins, Sparkles, Check, CreditCard, Zap, Shield, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getTokenPackages, getTokenCosts } from "../../services/api";
import './ContentStyles.css';

const getIsFrench = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host === "broderiemagique.com" || host === "www.broderiemagique.com";
};

function BuyTokensContent() {
  const { t, i18n } = useTranslation();
  const isFrench = getIsFrench() || i18n.language.startsWith("fr");
  const currency = isFrench ? "eur" : "usd";
  const currencySymbol = isFrench ? "€" : "$";
  const currencyLabel = isFrench ? "EUR" : "USD";
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingPackageId, setProcessingPackageId] = useState(null);
  const [tokenCosts, setTokenCosts] = useState({ ai_image_generation: 2 });
  // Payment method selector modal
  const [paymentModalPackageId, setPaymentModalPackageId] = useState(null);

  useEffect(() => {
    loadPackages();
    loadTokenCosts();
  }, []);

  const loadTokenCosts = async () => {
    try {
      const data = await getTokenCosts();
      if (data && data.costs) {
        setTokenCosts(data.costs);
      }
    } catch (error) {
      console.error("Failed to load token costs:", error);
    }
  };

  const loadPackages = async () => {
    try {
      setLoading(true);
      const data = await getTokenPackages();
      setPackages(data);
      setError("");
    } catch (err) {
      setError(t("buyTokens.failedLoadPackages"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (packageId) => {
    // Open payment method selector modal
    setPaymentModalPackageId(packageId);
  };

  const handleStripeCheckout = async (packageId) => {
    try {
      setPaymentModalPackageId(null);
      setProcessingPackageId(packageId);

      const { default: api } = await import("../../services/api");
      const response = await api.post("/payment/create-checkout/", {
        package_id: packageId,
        currency: currency,
        frontend_origin: window.location.origin,
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error(t("buyTokens.noCheckoutUrl"));
      }
    } catch (err) {
      console.error("Stripe checkout error:", err);
      setError(err.response?.data?.error || t("buyTokens.failedCheckout"));
      setProcessingPackageId(null);
    }
  };

  const handlePayPalCheckout = async (packageId) => {
    try {
      setPaymentModalPackageId(null);
      setProcessingPackageId(packageId);

      const { default: api } = await import("../../services/api");
      const response = await api.post("/payment/paypal/create-order/", {
        package_id: packageId,
        currency: currency,
        frontend_origin: window.location.origin,
      });

      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        throw new Error(t("buyTokens.noCheckoutUrl"));
      }
    } catch (err) {
      console.error("PayPal checkout error:", err);
      setError(err.response?.data?.error || t("buyTokens.failedCheckout"));
      setProcessingPackageId(null);
    }
  };

  const features = [
    {
      icon: Zap,
      title: t("buyTokens.featureInstantTitle"),
      description: t("buyTokens.featureInstantDesc"),
    },
    {
      icon: Shield,
      title: t("buyTokens.featureSecureTitle"),
      description: t("buyTokens.featureSecureDesc"),
    },
    {
      icon: CreditCard,
      title: t("buyTokens.featureFlexibleTitle"),
      description: t("buyTokens.featureFlexibleDesc"),
    },
    {
      icon: Sparkles,
      title: t("buyTokens.featureEmailTitle"),
      description: t("buyTokens.featureEmailDesc"),
    },
  ];

  return (
    <div className="content-wrapper">
      {/* Header */}
      <div className="content-header">
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "48px",
            height: "48px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px",
            marginBottom: "12px",
          }}
        >
          <Coins size={24} style={{ color: "white" }} />
        </div>

        <h1
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: "#111827",
            marginBottom: "8px",
          }}
        >
          {t("buyTokens.title")}
        </h1>

        <p style={{ fontSize: "12px", color: "#6b7280", margin: "0" }}>
          {t("buyTokens.tokensPerDesign", { count: tokenCosts.ai_image_generation })}
        </p>
      </div>

      <div className="content-main">
        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: "10px 12px",
              background: "#fee2e2",
              color: "#991b1b",
              borderRadius: "12px",
              marginBottom: "12px",
              fontSize: "12px",
            }}
          >
            {error}
          </div>
        )}

        {/* Packages Grid */}
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "3px solid #e5e7eb",
                borderTopColor: "#3b82f6",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          </div>
        ) : (
          <div className="tokens-grid">
            {packages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                onPurchase={() => handlePurchase(pkg.id)}
                processing={processingPackageId === pkg.id}
                t={t}
                isFrench={isFrench}
                currencySymbol={currencySymbol}
                currencyLabel={currencyLabel}
              />
            ))}
          </div>
        )}

        {/* Features Grid */}
        <div>
          <h2
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#111827",
              textAlign: "center",
              marginBottom: "12px",
              marginTop: "16px",
            }}
          >
            {t("buyTokens.whyBuy")}
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "10px",
            }}
          >
            {features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} />
            ))}
          </div>
        </div>
      </div>

      {/* Payment Method Modal */}
      {paymentModalPackageId && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px",
          }}
          onClick={() => setPaymentModalPackageId(null)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              width: "100%",
              maxWidth: "360px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#111827", margin: 0 }}>
                {t("buyTokens.choosePayment") || "Choose Payment Method"}
              </h3>
              <button
                onClick={() => setPaymentModalPackageId(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", padding: "4px" }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Stripe / Card */}
            <button
              onClick={() => handleStripeCheckout(paymentModalPackageId)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                padding: "14px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                background: "white",
                cursor: "pointer",
                marginBottom: "12px",
                transition: "border-color 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#6366f1")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
            >
              <div style={{ width: "40px", height: "40px", background: "#eef2ff", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <CreditCard size={20} style={{ color: "#6366f1" }} />
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                  {t("buyTokens.payWithCard") || "Pay with Card"}
                </div>
                <div style={{ fontSize: "11px", color: "#6b7280" }}>Visa, Mastercard, Amex via Stripe</div>
              </div>
            </button>

            {/* PayPal */}
            <button
              onClick={() => handlePayPalCheckout(paymentModalPackageId)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                padding: "14px 16px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                background: "white",
                cursor: "pointer",
                transition: "border-color 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#0070ba")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
            >
              <div style={{ width: "40px", height: "40px", background: "#e6f4ff", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {/* PayPal logo mark */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.75A.641.641 0 0 1 5.577 2.2h7.518c2.518 0 4.396.54 5.54 1.604.533.498.903 1.077 1.1 1.72.21.68.22 1.44.028 2.264v.002l-.001.004c-.272 1.234-.842 2.23-1.697 2.963-.838.72-1.938 1.224-3.268 1.497 1.02.28 1.74.828 2.133 1.628.395.8.43 1.79.1 2.94l-.002.008-.002.008-1.34 5.504a.641.641 0 0 1-.625.497H12.42a.641.641 0 0 1-.635-.744l1.338-5.498c.19-.66.18-1.17-.028-1.513-.204-.338-.616-.538-1.26-.608L9.82 14.49l-1.112 6.35a.641.641 0 0 1-.632.497z" fill="#003087"/>
                  <path d="M20.16 5.605c-.01.07-.02.14-.033.21-.84 4.32-3.72 5.81-7.4 5.81H10.9c-.448 0-.828.326-.9.77l-.96 6.09-.272 1.727a.474.474 0 0 0 .468.547h3.287c.392 0 .724-.285.786-.672l.032-.166.622-3.948.04-.216a.793.793 0 0 1 .783-.672h.493c3.195 0 5.7-1.298 6.432-5.052.306-1.57.148-2.88-.66-3.8a3.157 3.157 0 0 0-.89-.618z" fill="#0070ba"/>
                </svg>
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                  {t("buyTokens.payWithPayPal") || "Pay with PayPal"}
                </div>
                <div style={{ fontSize: "11px", color: "#6b7280" }}>PayPal balance, bank or card</div>
              </div>
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

function PackageCard({ pkg, onPurchase, processing, t, isFrench, currencySymbol, currencyLabel }) {
  const isPopular = pkg.name.toLowerCase().includes("pro");
  const displayPrice = isFrench && pkg.price_eur ? pkg.price_eur : pkg.price;
  const displayName = isFrench && pkg.name_fr ? pkg.name_fr : pkg.name;
  const displayFeatures = isFrench ? pkg.features : (pkg.features_en && pkg.features_en.length > 0 ? pkg.features_en : pkg.features);

  return (
    <div className="token-package" style={{ position: "relative" }}>
      {isPopular && (
        <div
          className="token-package-badge"
        >
          {t("buyTokens.mostPopular")}
        </div>
      )}

      <div style={{ marginBottom: "12px", textAlign: "center" }}>
        <h3
          style={{
            fontSize: "14px",
            fontWeight: "700",
            color: "#111827",
            marginBottom: "6px",
          }}
        >
          {displayName}
        </h3>

        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "4px" }}>
          <span
            style={{
              fontSize: "20px",
              fontWeight: "800",
              color: "#111827",
            }}
          >
            {currencySymbol}{parseFloat(displayPrice).toFixed(2)}
          </span>
          <span style={{ fontSize: "11px", color: "#6b7280" }}>{currencyLabel}</span>
        </div>

        <div
          style={{
            marginTop: "10px",
            padding: "6px 10px",
            background: "#f3f4f6",
            borderRadius: "8px",
            display: "inline-block",
          }}
        >
          <span style={{ fontSize: "14px", fontWeight: "700", color: "#111827" }}>
            {pkg.tokens}
          </span>
          <span style={{ fontSize: "11px", color: "#6b7280", marginLeft: "4px" }}>
            {t("buyTokens.tokens")}
          </span>
        </div>
      </div>

      {pkg.description && (
        <p
          style={{
            fontSize: "11px",
            color: "#6b7280",
            textAlign: "center",
            marginBottom: "12px",
            minHeight: "auto",
          }}
        >
          {pkg.description}
        </p>
      )}

      <button
        onClick={onPurchase}
        disabled={processing}
        className="token-buy-btn"
        style={{
          background: processing
            ? "#9ca3af"
            : isPopular
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : "#3b82f6",
        }}
      >
        {processing ? (
          <>
            <div
              style={{
                width: "14px",
                height: "14px",
                border: "2px solid rgba(255,255,255,0.3)",
                borderTopColor: "white",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            {t("buyTokens.processing")}
          </>
        ) : (
          <>
            {t("buyTokens.buyNow")}
          </>
        )}
      </button>

      {displayFeatures && displayFeatures.length > 0 && (
        <div style={{ marginTop: "12px", borderTop: "1px solid #e5e7eb", paddingTop: "10px" }}>
          {displayFeatures.map((feature, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <Check size={14} style={{ color: "#10b981", flexShrink: 0 }} />
              <span style={{ fontSize: "11px", color: "#374151" }}>{feature}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeatureCard({ feature }) {
  const Icon = feature.icon;

  return (
    <div
      style={{
        background: "white",
        borderRadius: "12px",
        padding: "12px",
        textAlign: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          background: "#ede9fe",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={20} style={{ color: "#7c3aed" }} />
      </div>

      <div style={{ textAlign: "left" }}>
        <h4 style={{ fontSize: "12px", fontWeight: "600", color: "#111827", marginBottom: "2px", margin: 0 }}>
          {feature.title}
        </h4>

        <p style={{ fontSize: "11px", color: "#6b7280", margin: 0 }}>{feature.description}</p>
      </div>
    </div>
  );
}

export default BuyTokensContent;
